import { toInputDate } from "@/utils/dateUtils";
import type { ReactNode } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import ServiceEntry from "./service-history/ServiceEntry";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTextColorClass } from "@/components/ui/text-coloring";
import { useLanguage } from "@/context/LanguageContext";
import { carDetailsLabels } from "@/i18n";
import ImageGallery from "./tire-info/ImageGallery";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { ServiceDetail } from "@/types/vehicleTest";
import { FileObject } from "@/types/shared/file";
import { GuestLockedSection } from "@/components/auth/GuestLockedSection";

interface ServiceHistoryProps {
  serviceImages?: FileObject[];
  serviceDetails?: ServiceDetail[];
  guestLocked?: boolean;
  onGuestLoginRequired?: () => void;
  /** When false, render content expanded (no accordion), e.g. for document/PDF view. */
  collapsible?: boolean;
  /** When true, show images on top and service details below in one column (no tabs). Used in DocumentView. */
  singleColumnLayout?: boolean;
  /** Optional: open clicked image in parent lightbox (DocumentView) */
  onOpenImage?: (args: { url: string; alt: string; index: number }) => void;
}

const ServiceHistory = ({
  serviceImages,
  serviceDetails,
  collapsible = true,
  singleColumnLayout = false,
  onOpenImage,
  guestLocked = false,
  onGuestLoginRequired,
}: ServiceHistoryProps) => {
  const isMobile = useIsMobile();
  const { baseText } = useTextColorClass();
  const { language } = useLanguage();
  const labels = carDetailsLabels(language);
  const sh = labels.serviceHistory;

  // Convert service images to string array
  const getServiceImages = (): string[] => {
    if (!serviceImages) return [];

    return serviceImages
      .filter((image) => image.url)
      .map((image) => image.url!);
  };

  const getServiceFullscreenImages = (): string[] => {
    if (!serviceImages) return [];
    return serviceImages
      .filter((image) => image.url)
      .map((image) => (image as any).fullSizeUrl || image.url!);
  };

  // Convert service details to array
  const getServiceDetails = (): Array<{
    id: string;
    date: string;
    mileage: string;
    serviceType?: string;
    notes?: string;
  }> => {
    if (!serviceDetails) return [];

    return serviceDetails.map((detail, index) => ({
      id: detail.id || `service_${index + 1}`,
      date:
        detail.date instanceof Date
          ? toInputDate(detail.date)
          : (() => {
              const raw = String(detail.date || "");
              // Backend sometimes sends ISO strings; show only YYYY-MM-DD
              if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
                return raw.slice(0, 10);
              }
              return raw;
            })(),
      mileage: detail.mileage.toString(),
      serviceType: detail.serviceType || undefined,
      notes: detail.notes || undefined,
    }));
  };

  const allServiceImages = getServiceImages();
  const allServiceFullscreenImages = getServiceFullscreenImages();
  const allServiceDetails = getServiceDetails();

  const hasImages = allServiceImages.length > 0;
  const hasDetails = allServiceDetails.length > 0;

  const imagesGalleryBlock = hasImages ? (
    <div>
      <h3 className={`text-base md:text-base font-semibold mb-3 ${baseText}`}>
        {sh.serviceDocumentation}
      </h3>
      <ImageGallery
        images={allServiceImages}
        fullscreenImages={allServiceFullscreenImages}
        title={sh.serviceDocumentation}
        onOpenImage={onOpenImage}
      />
    </div>
  ) : null;

  const detailsListBlock = hasDetails ? (
    <div>
      <h3 className={`text-base md:text-base font-semibold mb-3 ${baseText}`}>
        {sh.serviceDetails}
      </h3>
      <div className="space-y-4">
        {allServiceDetails.map((detail) => (
          <ServiceEntry
            key={detail.id}
            date={detail.date}
            mileage={detail.mileage}
            serviceType={detail.serviceType}
            notes={detail.notes}
          />
        ))}
      </div>
    </div>
  ) : null;

  const missingCopy = sh.missingServiceInfo ?? sh.noData ?? "";

  let content: ReactNode;

  if (!hasImages && !hasDetails) {
    content = (
      <p className={`text-sm md:text-sm ${baseText}`}>{missingCopy}</p>
    );
  } else if (singleColumnLayout) {
    content = (
      <div className="space-y-6">
        {imagesGalleryBlock}
        {detailsListBlock}
      </div>
    );
  } else if (hasImages && hasDetails) {
    content = (
      <div className="space-y-4">
        <Tabs defaultValue="images" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="images">
              {isMobile ? sh.images : sh.serviceImages}
            </TabsTrigger>
            <TabsTrigger value="details">
              {isMobile ? sh.details : sh.serviceDetailsTab}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="images" className="space-y-4 mt-4">
            {imagesGalleryBlock}
          </TabsContent>
          <TabsContent value="details" className="space-y-4 mt-4">
            {detailsListBlock}
          </TabsContent>
        </Tabs>
      </div>
    );
  } else if (hasImages) {
    content = <div className="space-y-4">{imagesGalleryBlock}</div>;
  } else {
    content = <div className="space-y-4">{detailsListBlock}</div>;
  }

  const displayContent = (
    <GuestLockedSection locked={guestLocked} onLoginRequired={onGuestLoginRequired}>
      {guestLocked ? <div className="min-h-[8rem]" aria-hidden /> : content}
    </GuestLockedSection>
  );

  if (!collapsible) {
    return (
      <div className={`rounded-lg backdrop-blur border bg-card text-card-foreground shadow-sm animate-fade-in`}>
        <div className="p-4 md:p-6">
          <h2 className={`font-semibold text-sm md:text-base mb-4 ${baseText}`}>{sh.title}</h2>
          {displayContent}
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-lg backdrop-blur border bg-card text-card-foreground shadow-sm animate-fade-in`}>
      <Accordion type="single" collapsible className="w-full">
        <AccordionItem value="service-history">
          <AccordionTrigger className={`px-4 py-4 md:py-3 hover:bg-accent/20 flex justify-between`}>
            <span className={`font-semibold text-base md:text-base ${baseText}`}>{sh.title}</span>
          </AccordionTrigger>
          <AccordionContent className={`p-4`}>
            {displayContent}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};

export default ServiceHistory;
